/**
 * WaypointEditor Component
 *
 * Right sidebar panel for editing waypoint properties
 */

import { useState, useEffect } from 'react';
import { X, MapPin, Settings as SettingsIcon, FileText } from 'lucide-react';
import { useMission } from '../hooks/useMission';
import { useWaypoints } from '../hooks/useWaypoints';
import { CommandSelector } from './CommandSelector';
import { COMMAND_INFO, MavCommand } from '@/constants/mavlink-commands';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface WaypointEditorProps {
  isOpen: boolean;
  onClose: () => void;
}

export function WaypointEditor({ isOpen, onClose }: WaypointEditorProps) {
  const { mission } = useMission();
  const { selectedWaypoint, updateWaypoint } = useWaypoints();

  // Local form state
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const [altitude, setAltitude] = useState(0);
  const [command, setCommand] = useState<MavCommand>(MavCommand.NAV_WAYPOINT);
  const [param1, setParam1] = useState(0);
  const [param2, setParam2] = useState(0);
  const [param3, setParam3] = useState(0);
  const [param4, setParam4] = useState(0);
  const [autocontinue, setAutocontinue] = useState(true);

  // Initialize form when waypoint changes
  useEffect(() => {
    if (selectedWaypoint) {
      setLabel(selectedWaypoint.label || '');
      setNotes(selectedWaypoint.notes || '');
      setLatitude(selectedWaypoint.latitude);
      setLongitude(selectedWaypoint.longitude);
      setAltitude(Number(selectedWaypoint.altitude.toFixed(1)));
      setCommand(selectedWaypoint.command);
      setParam1(selectedWaypoint.param1);
      setParam2(selectedWaypoint.param2);
      setParam3(selectedWaypoint.param3);
      setParam4(selectedWaypoint.param4);
      setAutocontinue(selectedWaypoint.autocontinue);
    }
  }, [selectedWaypoint]);

  const handleApply = () => {
    if (!selectedWaypoint) {
      toast.error('No waypoint selected');
      return;
    }

    // Validate coordinates
    if (latitude < -90 || latitude > 90) {
      toast.error('Latitude must be between -90 and 90');
      return;
    }
    if (longitude < -180 || longitude > 180) {
      toast.error('Longitude must be between -180 and 180');
      return;
    }

    updateWaypoint(selectedWaypoint.id, {
      label,
      notes,
      latitude,
      longitude,
      altitude,
      command,
      param1,
      param2,
      param3,
      param4,
      autocontinue,
    });

    toast.success('Waypoint updated');
  };

  const handleCancel = () => {
    // Reset form to current waypoint values
    if (selectedWaypoint) {
      setLabel(selectedWaypoint.label || '');
      setNotes(selectedWaypoint.notes || '');
      setLatitude(selectedWaypoint.latitude);
      setLongitude(selectedWaypoint.longitude);
      setAltitude(selectedWaypoint.altitude);
      setCommand(selectedWaypoint.command);
      setParam1(selectedWaypoint.param1);
      setParam2(selectedWaypoint.param2);
      setParam3(selectedWaypoint.param3);
      setParam4(selectedWaypoint.param4);
      setAutocontinue(selectedWaypoint.autocontinue);
    }
    onClose();
  };

  const commandInfo = COMMAND_INFO[command];

  if (!isOpen || !selectedWaypoint) return null;

  return (
    <div className="pointer-events-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-[60]"
        onClick={handleCancel}
      />

      {/* Slide-in Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-slate-900 border-l border-slate-700 shadow-2xl z-[70] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-400" />
            <div>
              <h2 className="text-sm font-semibold text-white">
                Edit Waypoint #{selectedWaypoint.sequence + 1}
              </h2>
              <p className="text-xs text-slate-400">
                {selectedWaypoint.id.substring(0, 8)}
              </p>
            </div>
          </div>
          <button
            onClick={handleCancel}
            className="text-slate-400 hover:text-white transition-colors p-1"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
          {/* Label & Notes Section */}
          <div>
            <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Description
            </h3>
            <div className="space-y-3">
              {/* Label */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Label
                </label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g., Takeoff Point, Survey Area 1"
                  className="w-full bg-slate-800 text-white rounded-md border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes or comments..."
                  rows={3}
                  className="w-full bg-slate-800 text-white rounded-md border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            </div>
          </div>

          {/* Position Section */}
          <div>
            <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Position
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {/* Latitude */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Latitude
                </label>
                <input
                  type="number"
                  value={latitude}
                  onChange={(e) => setLatitude(Number(e.target.value))}
                  step={0.000001}
                  min={-90}
                  max={90}
                  className="w-full bg-slate-800 text-white rounded-md border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Longitude */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Longitude
                </label>
                <input
                  type="number"
                  value={longitude}
                  onChange={(e) => setLongitude(Number(e.target.value))}
                  step={0.000001}
                  min={-180}
                  max={180}
                  className="w-full bg-slate-800 text-white rounded-md border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Altitude */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Altitude
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={altitude}
                    onChange={(e) => setAltitude(Number(Number(e.target.value).toFixed(1)))}
                    step={1}
                    min={0}
                    className="w-full bg-slate-800 text-white rounded-md border border-slate-600 px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                    m
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Command Section */}
          <div>
            <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-3 flex items-center gap-2">
              <SettingsIcon className="w-4 h-4" />
              Command
            </h3>
            <div className="space-y-3">
              {/* Command Selector */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Command Type
                </label>
                <CommandSelector
                  value={command}
                  onChange={setCommand}
                  vehicleType={mission?.vehicleType}
                />
              </div>

              {/* Command Description */}
              {commandInfo && (
                <div className="bg-blue-900/20 border border-blue-700/50 rounded-md p-3">
                  <p className="text-xs text-blue-200">
                    {commandInfo.description}
                  </p>
                </div>
              )}

              {/* Parameters */}
              <div className="grid grid-cols-2 gap-3">
                {commandInfo?.params
                  .filter((paramInfo) => paramInfo.name !== 'Empty')
                  .map((paramInfo) => (
                    <div key={paramInfo.index} className="col-span-2">
                      <label className="block text-sm font-medium text-slate-300 mb-1">
                        {paramInfo.name}
                        {paramInfo.unit && (
                          <span className="text-slate-500 ml-1">
                            ({paramInfo.unit})
                          </span>
                        )}
                      </label>
                      <input
                        type="number"
                        value={
                          paramInfo.index === 1
                            ? param1
                            : paramInfo.index === 2
                            ? param2
                            : paramInfo.index === 3
                            ? param3
                            : param4
                        }
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          if (paramInfo.index === 1) setParam1(val);
                          else if (paramInfo.index === 2) setParam2(val);
                          else if (paramInfo.index === 3) setParam3(val);
                          else setParam4(val);
                        }}
                        step={0.1}
                        min={paramInfo.min}
                        max={paramInfo.max}
                        className="w-full bg-slate-800 text-white rounded-md border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        {paramInfo.description}
                      </p>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Mission Control */}
          <div>
            <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-3">
              Mission Control
            </h3>
            <div className="flex items-center justify-between bg-slate-800 rounded-md px-3 py-2 border border-slate-600">
              <div>
                <p className="text-sm font-medium text-white">Auto Continue</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Automatically proceed to next waypoint
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAutocontinue(!autocontinue)}
                className={cn(
                  'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                  autocontinue ? 'bg-blue-600' : 'bg-slate-600'
                )}
              >
                <span
                  className={cn(
                    'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                    autocontinue ? 'translate-x-6' : 'translate-x-1'
                  )}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-slate-700 bg-slate-800">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
          >
            Apply Changes
          </button>
        </div>
      </div>
    </div>
  );
}
